// =============================================
// src/schemas/vehicleSchema.js
// LEAN SINGLE SOURCE OF TRUTH - Vehicle Data Structure
// =============================================

/**
 * STANDARDIZED VEHICLE SCHEMA
 * Eliminates frontend-backend inconsistencies
 * Single source of truth for all vehicle operations
 */

// ============================
// CORE VEHICLE STRUCTURE
// ============================

export const VEHICLE_BASE_SCHEMA = {
    // Basic Information
    title: '',
    brand: '',
    model: '',
    bodyType: '',
    year: null,
    mileage: null,
    color: '',
    fuelType: 'benzin',
    transmission: 'manuel',
    description: '',

    // Financial Information (ERP)
    purchasePrice: null,
    salePrice: null,
    purchaseDate: null,
    saleDate: null,

    // Legal Information
    plateNumber: '',
    chassisNumber: '',
    engineNumber: '',

    // Tramer Information
    hasTramer: false,
    tramerAmount: 0,
    tramerDescription: '',

    // Status Management
    status: 'active', // active, sold, reserved, archived

    // System Fields
    customId: null,
    viewCount: 0,
    addedBy: null,
    createdAt: null,
    updatedAt: null,

    // Arrays
    images: [],
    features: [],
    expenses: [],
    documents: []
};

// ============================
// PERSON SCHEMA (LEAN DRY)
// ============================

export const PERSON_BASE_SCHEMA = {
    name: '',
    phone: '',
    email: '',
    address: '',
    idNumber: ''
};

// ============================
// TRAFFIC INSURANCE SCHEMA
// ============================

export const TRAFFIC_INSURANCE_SCHEMA = {
    // Basic Insurance Info
    hasInsurance: false,
    startDate: null,
    endDate: null,
    insuranceCompany: '',
    paymentCardName: '',
    insuranceAmount: 0,
    policyDocument: '',
    policyNumber: '',
    notes: '',

    // Cancellation Info (for SOLD/ARCHIVED status)
    isCancelled: false,
    cancellationDate: null,
    cancellationPolicyDocument: '',
    refundAmount: 0,
    cancellationReason: '',
    cancellationNotes: ''
};

// ============================
// COMPLETE VEHICLE SCHEMA
// ============================

export const COMPLETE_VEHICLE_SCHEMA = {
    ...VEHICLE_BASE_SCHEMA,

    // Person Information
    purchasedFrom: PERSON_BASE_SCHEMA,
    soldTo: PERSON_BASE_SCHEMA,

    // Traffic Insurance
    trafficInsurance: TRAFFIC_INSURANCE_SCHEMA
};

// ============================
// VALIDATION SCHEMAS
// ============================

export const REQUIRED_FIELDS = {
    basic: ['plateNumber', 'chassisNumber', 'engineNumber', 'purchasePrice'],
    forSale: ['brand', 'model', 'year', 'salePrice'],
    forSold: ['soldTo.name'],
    forArchived: []
};

export const FIELD_CONSTRAINTS = {
    plateNumber: { minLength: 2, maxLength: 15, pattern: /^[0-9]{2}[A-Z]{1,3}[0-9]{1,4}$/ },
    chassisNumber: { length: 17, pattern: /^[A-HJ-NPR-Z0-9]{17}$/ },
    engineNumber: { minLength: 3, maxLength: 20 },
    year: { min: 1950, max: new Date().getFullYear() + 1 },
    mileage: { min: 0, max: 999999 },
    purchasePrice: { min: 1, max: 999999999 },
    salePrice: { min: 1, max: 999999999 },
    tramerAmount: { min: 0, max: 999999999 }
};

// ============================
// ENUM DEFINITIONS
// ============================

export const VEHICLE_ENUMS = {
    status: ['active', 'sold', 'reserved', 'archived'],

    fuelType: ['benzin', 'dizel', 'lpg', 'elektrik', 'hibrit', 'benzin+lpg'],

    transmission: ['manuel', 'otomatik', 'yarı otomatik'],

    bodyType: ['sedan', 'hatchback', 'suv', 'coupe', 'cabriolet', 'station wagon', 'pickup', 'minivan'], // ✅ FIX: lowercase

    expenseTypes: [
        'bakim_onarim', 'yakit', 'temizlik_detay', 'hasar_onarim',
        'sigorta', 'muayene', 'vergi_harc', 'park_otopark', 'diger'
    ],

    documentTypes: [
        'alis_sozlesmesi', 'satis_sozlesmesi', 'ruhsat',
        'trafik_sigortasi', 'sigorta_iptal_policesi', 'diger'
    ],

    insuranceCompanies: [
        'Aksigorta', 'Allianz', 'Anadolu Sigorta', 'Axa Sigorta',
        'Güneş Sigorta', 'HDI Sigorta', 'Mapfre Sigorta',
        'Sompo Sigorta', 'Türkiye Sigorta', 'Zurich Sigorta', 'Diğer'
    ],

    cancellationReasons: [
        'Araç Satışı', 'Sahibi Değişti', 'Başka Şirkete Geçiş', 'Diğer'
    ],

    // ✅ NEW: Display names for UI
    bodyTypeDisplayNames: {
        'sedan': 'Sedan',
        'hatchback': 'Hatchback',
        'suv': 'SUV',
        'coupe': 'Coupe',
        'cabriolet': 'Cabriolet',
        'station wagon': 'Station Wagon',
        'pickup': 'Pickup',
        'minivan': 'Minivan'
    }
};

// ============================
// SCHEMA FACTORIES (LEAN CONSTRUCTORS)
// ============================

/**
 * Create empty vehicle with default values
 */
export const createEmptyVehicle = () => ({
    ...COMPLETE_VEHICLE_SCHEMA,
    purchasedFrom: { ...PERSON_BASE_SCHEMA },
    soldTo: { ...PERSON_BASE_SCHEMA },
    trafficInsurance: { ...TRAFFIC_INSURANCE_SCHEMA }
});

/**
 * Create vehicle for specific purpose
 */
export const createVehicleForPurpose = (purpose = 'add') => {
    const vehicle = createEmptyVehicle();

    switch (purpose) {
        case 'add':
            vehicle.purchaseDate = new Date().toISOString().split('T')[0];
            vehicle.trafficInsurance.hasInsurance = true;
            vehicle.purchasedFrom.name = 'Stok Girişi';
            break;

        case 'edit':
            // Keep as is - will be populated from API
            break;

        case 'sale':
            vehicle.status = 'sold';
            vehicle.saleDate = new Date().toISOString().split('T')[0];
            break;

        case 'archive':
            vehicle.status = 'archived';
            vehicle.trafficInsurance.isCancelled = true;
            break;
    }

    return vehicle;
};

// ============================
// SCHEMA VALIDATION HELPERS
// ============================

/**
 * Validate vehicle data against schema
 */
export const validateVehicleSchema = (vehicle, purpose = 'basic') => {
    const errors = [];
    const requiredFields = REQUIRED_FIELDS[purpose] || REQUIRED_FIELDS.basic;

    // Check required fields
    requiredFields.forEach(field => {
        const value = getNestedValue(vehicle, field);
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            errors.push(`${field} is required for ${purpose}`);
        }
    });

    // Check field constraints
    Object.entries(FIELD_CONSTRAINTS).forEach(([field, constraints]) => {
        const value = vehicle[field];
        if (value !== null && value !== undefined && value !== '') {
            if (constraints.min && value < constraints.min) {
                errors.push(`${field} must be at least ${constraints.min}`);
            }
            if (constraints.max && value > constraints.max) {
                errors.push(`${field} must be at most ${constraints.max}`);
            }
            if (constraints.length && value.toString().length !== constraints.length) {
                errors.push(`${field} must be exactly ${constraints.length} characters`);
            }
            if (constraints.pattern && !constraints.pattern.test(value)) {
                errors.push(`${field} format is invalid`);
            }
        }
    });

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Get nested object value safely
 */
const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current && current[key], obj);
};

// ============================
// LEAN DEFAULTS
// ============================

export const DEFAULT_VALUES = {
    fuelType: 'benzin',
    transmission: 'manuel',
    status: 'active',
    hasTramer: false,
    tramerAmount: 0,
    viewCount: 0,

    // Traffic Insurance Defaults
    trafficInsuranceHasInsurance: true,
    trafficInsuranceAmount: 0,
    trafficInsuranceRefundAmount: 0,
    trafficInsuranceCancelled: false,

    // Person Defaults
    purchasedFromName: 'Stok Girişi'
};

export default {
    VEHICLE_BASE_SCHEMA,
    PERSON_BASE_SCHEMA,
    TRAFFIC_INSURANCE_SCHEMA,
    COMPLETE_VEHICLE_SCHEMA,
    REQUIRED_FIELDS,
    FIELD_CONSTRAINTS,
    VEHICLE_ENUMS,
    createEmptyVehicle,
    createVehicleForPurpose,
    validateVehicleSchema,
    DEFAULT_VALUES
};

export const BODY_TYPE_MAPPING = {
    // DB Value → Display Value
    'sedan': 'Sedan',
    'hatchback': 'Hatchback',
    'suv': 'SUV',
    'coupe': 'Coupe',
    'minivan': 'Minivan',
    'pickup': 'Pickup',
    'cabriolet': 'Cabriolet',
    'station wagon': 'Station Wagon'
};

// Reverse mapping
export const BODY_TYPE_DB_VALUES = Object.fromEntries(
    Object.entries(BODY_TYPE_MAPPING).map(([db, display]) => [display, db])
);